import { inject, injectable } from "inversify";
import {
  ClientPortalUserDetailsModel,
  ClientPortalUserModel,
  ICPLAuthClientPortalUsersClient,
  NewClientPortalUserModel,
  TwoFactorAuthSetupModel,
  ExternalPasswordChangeModel,
} from "./api/cplauthapi/CPLAuthAPI";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";

export interface IClientPortalUsersService {
  getProfile(): Promise<ClientPortalUserModel>;
  acceptTermsAndConditions(): Promise<void>;
  getTwoFactorAuthSetupData(): Promise<TwoFactorAuthSetupModel>;
  verifyOneTimePasswordSetup(oneTimePassword: string): Promise<boolean>;
  sendTwoFactorAuthEmailSetupCode(): Promise<void>;
  verifyTwoFactorAuthEmailSetup(verificationCode: string): Promise<boolean>;
  sendEmailVerificationCode(emailAddress: string, companyID: string): Promise<void>;
  verifyEmail(emailAddress: string, verificationCode: string, companyID: string): Promise<void>;
  createNewClientPortalUser(newClientPortalUserModel: NewClientPortalUserModel): Promise<void>;
  changeClientPortalUserDetails(clientPortalUserDetailsModel: ClientPortalUserDetailsModel): Promise<void>;
  changeClientPortalUserPassword(newPassword: string): Promise<void>;
  changeClientPortalUserEmail(emailAddress: string, verificationCode: string, companyID: string): Promise<void>;
  linkClientAccount(clientAccountNumber: string, emailAddress: string): Promise<boolean>;
  getLinkedClientAccounts(clientPortalUserID: string, companyID: string);
  unlinkClientAccount(clientPortalUserID: string, clientID: string): Promise<void>;
  resetPassword(emailAddress: string): Promise<void>;
  externalPasswordChange(passwordModel: ExternalPasswordChangeModel): Promise<void>;
}

@injectable()
export class ClientPortalUsersService implements IClientPortalUsersService {
  @inject(SERVICE_TYPES.CPLAuthClientPortalUsersClient)
  cplAuthClientPortalUsersClient!: ICPLAuthClientPortalUsersClient;

  getProfile(): Promise<ClientPortalUserModel> {
    return this.cplAuthClientPortalUsersClient.getProfile();
  }

  async acceptTermsAndConditions(): Promise<void> {
    return await this.cplAuthClientPortalUsersClient.acceptTermsAndConditions();
  }

  async getTwoFactorAuthSetupData(): Promise<TwoFactorAuthSetupModel> {
    return await this.cplAuthClientPortalUsersClient.getTwoFactorAuthSetupData();
  }

  async verifyOneTimePasswordSetup(oneTimePassword: string): Promise<boolean> {
    return await this.cplAuthClientPortalUsersClient.verifyOneTimePasswordSetup(oneTimePassword);
  }

  async sendTwoFactorAuthEmailSetupCode(): Promise<void> {
    await this.cplAuthClientPortalUsersClient.sendTwoFactorAuthEmailSetupCode();
  }

  async verifyTwoFactorAuthEmailSetup(verificationCode: string): Promise<boolean> {
    return await this.cplAuthClientPortalUsersClient.verifyTwoFactorAuthEmailSetup(verificationCode);
  }

  async sendEmailVerificationCode(emailAddress: string, companyID: string): Promise<void> {
    return await this.cplAuthClientPortalUsersClient.sendEmailVerificationCode(emailAddress, companyID);
  }

  async verifyEmail(emailAddress: string, verificationCode: string, companyID: string): Promise<void> {
    return await this.cplAuthClientPortalUsersClient.verifyEmail(emailAddress, verificationCode, companyID);
  }

  async createNewClientPortalUser(newClientPortalUserModel: NewClientPortalUserModel): Promise<void> {
    return await this.cplAuthClientPortalUsersClient.createNewClientPortalUser(newClientPortalUserModel);
  }

  async changeClientPortalUserDetails(clientPortalUserDetailsModel: ClientPortalUserDetailsModel): Promise<void> {
    return await this.cplAuthClientPortalUsersClient.changeClientPortalUserDetails(clientPortalUserDetailsModel);
  }

  async changeClientPortalUserPassword(newPassword: string): Promise<void> {
    return await this.cplAuthClientPortalUsersClient.changeClientPortalUserPassword(newPassword);
  }

  async changeClientPortalUserEmail(emailAddress: string, verificationCode: string, companyID: string): Promise<void> {
    return await this.cplAuthClientPortalUsersClient.changeClientPortalUserEmail(
      emailAddress,
      verificationCode,
      companyID
    );
  }

  async linkClientAccount(clientAccountNumber: string, emailAddress: string): Promise<boolean> {
    return await this.cplAuthClientPortalUsersClient.linkClientAccount(clientAccountNumber, emailAddress);
  }

  async getLinkedClientAccounts(clientPortalUserID: string, companyID: string) {
    return await this.cplAuthClientPortalUsersClient.getLinkedClientAccounts(clientPortalUserID, companyID);
  }

  async unlinkClientAccount(clientPortalUserID: string, clientID: string): Promise<void> {
    return await this.cplAuthClientPortalUsersClient.unlinkClientAccount(clientPortalUserID, clientID);
  }

  async resetPassword(emailAddress: string): Promise<void> {
    return await this.cplAuthClientPortalUsersClient.resetPassword(emailAddress);
  }

  async externalPasswordChange(passwordModel: ExternalPasswordChangeModel): Promise<void> {
    return this.cplAuthClientPortalUsersClient.externalPasswordChange(passwordModel);
  }
}
